import React, { useEffect, useState, useCallback, MouseEvent } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { Functions, getFunctions } from 'firebase/functions';
import { Firestore, getFirestore } from 'firebase/firestore';
import { Auth, getAuth, onAuthStateChanged, signInWithCustomToken, User } from 'firebase/auth';
import { GTMProvider } from '@elgorditosalsero/react-gtm-hook';
import { ISnippetsParams } from '@elgorditosalsero/react-gtm-hook/dist/models/GoogleTagManager';
import AppProvider from '@contexts/app';
import ProductProvider from './contexts/product';
import VersionPage from '@components/pages/VersionPage';
import LoginPage from '@components/pages/LoginPage';
import FAQPage from '@components/pages/FAQPage';
import InitPage from '@components/pages/InitPage';
import ErrorPage from '@components/pages/ErrorPage';
import {
	StartClubPPVFlow,
	StartClubSubscriptionFlow,
	StartSubscriptionFlow,
	StartSubscriptionOldFlow,
} from './flows';
import FallbackComponent from '@components/Fallback';
import FAQDialog from '@components/FAQ/FAQDialog';
import { Logger } from '@utils/logger';
import * as Sentry from '@sentry/react';
import AppGTMStart from '@components/AppGTMStart';
import BrandingProvider from './contexts/branding';
import PrivatePage from '@components/pages/PrivatePage';
import AppThemeProvider from './contexts/apptheme';
import styled from 'styled-components';
import { TokenData } from './types/types';

const Container = styled.main`
	display: flex;
	flex: 1;
	flex-direction: column;
	color: ${({ theme }) => theme.textColor};
	background-color: ${({ theme }) => theme.bgColor};
`;

const initialGtmParams: ISnippetsParams = {
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	id: import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID!,
	injectScript: false,
};

if (
	import.meta.env.VITE_GOOGLE_TAG_MANAGER_AUTH &&
	import.meta.env.VITE_GOOGLE_TAG_MANAGER_PREVIEW
) {
	initialGtmParams.environment = {
		gtm_auth: import.meta.env.VITE_GOOGLE_TAG_MANAGER_AUTH,
		gtm_preview: import.meta.env.VITE_GOOGLE_TAG_MANAGER_PREVIEW,
	};
}

const logger = new Logger('App');

const {
	VITE_FIREBASE_API_KEY,
	VITE_FIREBASE_AUTH_DOMAIN,
	VITE_FIREBASE_DATABASE_URL,
	VITE_FIREBASE_PROJECT_ID,
	VITE_FIREBASE_STORAGE_BUCKET,
	VITE_FIREBASE_MESSAGING_SENDER_ID,
	VITE_FIREBASE_APP_ID,
	VITE_FIREBASE_MEASUREMENT_ID,
	VITE_FIREBASE_FUNCTION_REGION,
} = import.meta.env;
declare global {
	interface Window {
		app: unknown;
		firebaseAuth: unknown;
		firebaseFirestore: unknown;
		firebaseFunctions: unknown;
	}
}

const firebaseConfig = {
	apiKey: VITE_FIREBASE_API_KEY,
	authDomain: VITE_FIREBASE_AUTH_DOMAIN,
	databaseURL: VITE_FIREBASE_DATABASE_URL,
	projectId: VITE_FIREBASE_PROJECT_ID,
	storageBucket: VITE_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: VITE_FIREBASE_MESSAGING_SENDER_ID,
	appId: VITE_FIREBASE_APP_ID,
	measurementId: VITE_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

function App() {
	const [showFaq, setShowFaq] = useState(false);
	const [tokens, setTokens] = useState<TokenData>();
	const [firebaseAuth, setFirebaseAuth] = useState<Auth | undefined>();
	const [, setFirebaseUser] = useState<User | null>();
	const [firebaseFirestore, setFirebaseFirestore] = useState<Firestore | undefined>();
	const [firebaseFunctions, setFirebaseFunctions] = useState<Functions | undefined>();
	const [gtmParams, setGtmParams] = useState<ISnippetsParams>(initialGtmParams);

	/* oxlint-disable react/set-state-in-effect */
	useEffect(() => {
		setFirebaseFunctions(getFunctions(app, VITE_FIREBASE_FUNCTION_REGION));
		setFirebaseFirestore(getFirestore());
		setFirebaseAuth(getAuth());

		// Delay injecting GTM until later after Cookiebot has been initialized
		const timeoutRef = setTimeout(() => {
			setGtmParams({
				...initialGtmParams,
				injectScript: true,
			});
		}, 1000); // Arbitrary timeout value

		return () => clearTimeout(timeoutRef);
	}, []);
	/* oxlint-enable react/set-state-in-effect */

	useEffect(() => {
		if (!firebaseAuth) {
			return;
		}

		return onAuthStateChanged(firebaseAuth, setFirebaseUser);
	}, [firebaseAuth]);

	useEffect(() => {
		async function asyncSignInToFirebase() {
			if (tokens?.firebaseCustomToken && firebaseAuth) {
				try {
					await signInWithCustomToken(firebaseAuth, tokens.firebaseCustomToken);
				} catch (error) {
					logger.error('Firebase sign-in failed', error);
					Sentry.captureException(error);
				}
			}
		}
		asyncSignInToFirebase();
	}, [tokens, firebaseAuth]);

	const showFAQ = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		setShowFaq(true);
	}, []);

	const closeFAQ = useCallback(() => {
		setShowFaq(false);
	}, []);

	return (
		<GTMProvider state={gtmParams}>
			{gtmParams?.injectScript ? <AppGTMStart /> : null}
			<AppProvider
				logger={logger}
				firebaseFirestore={firebaseFirestore}
				firebaseAuth={firebaseAuth}
				firebaseFunctions={firebaseFunctions}
				tokens={tokens}
				setTokens={setTokens}
				showFAQ={showFAQ}
			>
				<BrandingProvider>
					<AppThemeProvider>
						<ProductProvider>
							<Router>
								<div className="App" data-testid="App">
									<Sentry.ErrorBoundary fallback={FallbackComponent}>
										<Container>
											<Routes>
												<Route
													path="/"
													element={<PrivatePage element={<InitPage />} />}
												/>
												<Route
													path="/:teamName"
													element={<PrivatePage element={<InitPage />} />}
												/>
												<Route
													path="/:teamName/ppv/*"
													element={
														<PrivatePage
															element={<StartClubPPVFlow />}
														/>
													}
												/>
												<Route
													path="/:teamName/subscription/*"
													element={
														<PrivatePage
															element={<StartClubSubscriptionFlow />}
														/>
													}
												/>
												<Route
													path="/subscription/*"
													element={
														<PrivatePage
															element={<StartSubscriptionFlow />}
														/>
													}
												/>
												<Route
													path="/subscription-legacy/*"
													element={
														<PrivatePage
															element={<StartSubscriptionOldFlow />}
														/>
													}
												/>
												<Route
													path="/login"
													element={
														<LoginPage location={window.location} />
													}
												/>
												<Route
													path="/login/:redirectOnSuccess"
													element={
														<LoginPage location={window.location} />
													}
												/>
												<Route
													path="/register"
													element={
														<LoginPage
															location={window.location}
															register
														/>
													}
												/>
												<Route path="/faq" element={<FAQPage />} />
												<Route path="/version" element={<VersionPage />} />
												<Route path="/error" element={<ErrorPage />} />
												<Route
													path="*"
													element={<ErrorPage status={404} />}
												/>
											</Routes>
										</Container>
										{showFaq && <FAQDialog onClose={closeFAQ} />}
									</Sentry.ErrorBoundary>
								</div>
							</Router>
						</ProductProvider>
					</AppThemeProvider>
				</BrandingProvider>
			</AppProvider>
		</GTMProvider>
	);
}

export default Sentry.withProfiler(App);
